import { Stack, Typography } from "@mui/material"
import { Dispatch, SetStateAction } from "react"

import { uuid } from "uuidv4"

import { ModifiedIncident } from ".."
import { Checkbox } from "src/components/common/checkbox"
import { create } from "src/helpers/bem"
import { formatLocaleDate } from "src/helpers/date"
import { formatPrice } from "src/helpers/price"
import { useTranslation } from "src/hooks/translation"
import { Dialogs, useDialog } from "src/states/dialogs"
import { useLocale } from "src/states/locale"
import { xRMApiIncidentPosition, xRMWebshopOrderedItem } from "src/types/xRM"

import styles from "./ProductRow.module.scss"

const bem = create(styles, "ProductRow")

type ProductRowProps = {
  selectedBookingId: string
  incident: ModifiedIncident
  item: xRMWebshopOrderedItem
  setIncident: Dispatch<SetStateAction<ModifiedIncident>>
}

export const ProductRow = ({
  item,
  incident,
  setIncident,
  selectedBookingId,
}: ProductRowProps) => {
  const locale = useLocale()
  const { messages: translations } = useTranslation()
  const { disabled: isReadOnly } = useDialog(
    "incidentDialog",
  ) as Dialogs["incidentDialog"]

  const { id: productId } = item
  const { incidentPositions } = incident
  const statusTranslations = translations.pages.orderedServices.status

  const statusLabel =
    item.stornoStatus === "Active"
      ? statusTranslations.edited
      : item.stornoStatus === "Closed"
        ? statusTranslations.cancelled
        : statusTranslations.cancelRequest

  const handleCheckboxChange = () => {
    if (!productId) {
      return
    }

    const isSelected = incidentPositions?.some(
      (position) => position.salesorderDetailId === productId,
    )
    let updatedSelectedItemIds: xRMApiIncidentPosition[] = []
    if (isSelected) {
      updatedSelectedItemIds =
        incidentPositions?.filter((p) => p.salesorderDetailId !== productId) ??
        []
    } else {
      updatedSelectedItemIds = [
        ...(incidentPositions ?? []),
        {
          id: uuid(),
          salesorderDetailId: productId,
          salesorderId: selectedBookingId,
        },
      ]
    }
    setIncident((prev) => ({
      ...prev,
      incidentPositions: updatedSelectedItemIds,
    }))
  }

  return (
    <Stack className={bem()} direction={"column"}>
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="row" spacing={1}>
          <Checkbox
            disabled={isReadOnly}
            checked={
              productId !== undefined &&
              incidentPositions?.some(
                (position) => position.salesorderDetailId === productId,
              )
            }
            onChange={handleCheckboxChange}
          />
          <Typography className={bem("title")} variant="h6">
            {item.name}
          </Typography>

          <Typography className={bem("unitPrice")}>
            {formatPrice(item.unitPrice, locale)} / {item.unit}
          </Typography>
        </Stack>

        <Typography className={bem("totalPrice")}>
          {formatPrice((item.unitPrice ?? 0) * (item.quantity ?? 0), locale)}
        </Typography>
      </Stack>
      <Stack direction={"row"} spacing={1} sx={{ pl: 7 }}>
        <div className={bem("chip")}>
          <Typography>
            {item.quantity} {item.unit}
          </Typography>
        </div>
        <div className={bem("chip")}>
          <Typography>
            {formatLocaleDate(locale, item.orderDate, "dd.MM.yyyy")}
          </Typography>
        </div>
        {item.stornoStatus && (
          <div
            className={bem(
              "chip",
              item.stornoStatus === "Active" ? "success" : "error",
            )}
          >
            <Typography>{statusLabel}</Typography>
          </div>
        )}
      </Stack>
    </Stack>
  )
}

export default ProductRow
